// =====================================================================
// coreOrder — rx.k 웹 체크아웃 → 컨트롤 코어(core-place-order) 유입 클라이언트
// ---------------------------------------------------------------------
//  · 정본(source of truth)은 컨트롤 코어 서버(core_orders)다.
//    localStorage 는 "영수증 사본"으로만 보관한다(주문완료 화면 표시용).
//  · 가격은 서버가 core_products.msrp_bdt 로 재계산한다 — 여기서 보내지 않는다.
//  · publishable(VITE_CORE_ANON)은 공개키라 브라우저 노출이 안전하다.
// =====================================================================
import { getFbCookies } from './tracking';
import { getAttribution } from './traffic';

const CORE_URL = import.meta.env.VITE_CORE_URL ?? '';
const CORE_ANON = import.meta.env.VITE_CORE_ANON ?? '';

// Storefront slug → core_products.sku. Filled from rxk_product_content (each
// product's slug + sku) when the catalog loads (fetchProducts), so a new
// product is orderable as soon as it has content in the admin. The entries
// below only cover the moment before that first load.
const SLUG_TO_SKU: Record<string, string> = {
  'skin-priming-booster-essence': 'RXK-BST50',
  'daily-rescue-serum': 'RXK-SRM50',
  'barrier-seal-cream': 'RXK-CRM50',
  '3-step-set': 'SET-3STEP',
};

export function registerProductSlugs(rows: { slug: string; sku: string }[]): void {
  for (const r of rows) if (r.slug && r.sku) SLUG_TO_SKU[r.slug] = r.sku;
}

export function slugToSku(slug: string): string | null {
  return SLUG_TO_SKU[slug] ?? null;
}

export interface PlaceOrderCustomer {
  name: string;
  phone: string;
  address: string;
  note?: string;
  // Optional — only used server-side to auto-create a pending invoice
  // request for this order. Guest checkouts without an email simply get
  // no auto-invoice (no one to email it to).
  email?: string;
  // Which shipping zone — server recomputes the actual fee from this
  // (never trusts a client-sent amount), same rule checkout uses to
  // display it (free >= 3000 BDT subtotal, else 60/120 by city).
  city?: 'Dhaka' | 'Outside Dhaka';
}

export interface PlaceOrderItem {
  sku: string;
  qty: number;
}

export interface PlaceOrderInput {
  customer: PlaceOrderCustomer;
  items: PlaceOrderItem[];
  // 'cod' or a core_payment_gateways.gateway_key — the server re-validates
  // that any non-'cod' value is a currently-enabled gateway regardless of
  // what's sent here. Defaults to 'cod' if omitted.
  paymentMethod?: string;
  // Logged-in customer's Supabase access token, if any — verified
  // server-side (never trusted as-is) to attach the order to their
  // account for order history + mileage rewards. Omit for guest checkout.
  customerAccessToken?: string;
  // Customer-entered coupon code, if any. Omit to let the server fall back
  // to any active automatic (no-code) discount instead. The server never
  // trusts a client-computed discount amount — it looks the code up in
  // rxk_discounts itself and recalculates.
  couponCode?: string;
}

// 정직한 에러 분류 — 호출 측이 i18n 키로 매핑한다.
export type PlaceOrderErrorCode =
  | 'insufficient_stock' // 재고 부족 (409)
  | 'cod_blocked' // 반복 COD 취소 이력 — 온라인 결제 유도
  | 'network' // 네트워크/타임아웃/연결 실패
  | 'config' // 코어 URL/키 미설정
  | 'validation' // 서버가 요청 거절(400)
  | 'server'; // 5xx 등 기타 서버 오류

export interface PlaceOrderSuccess {
  ok: true;
  orderNo: string | null;
  orderId: string;
  discountAmountBdt: number;
  shippingBdt: number;
}

export interface PlaceOrderFailure {
  ok: false;
  code: PlaceOrderErrorCode;
}

export type PlaceOrderResult = PlaceOrderSuccess | PlaceOrderFailure;

/**
 * 컨트롤 코어에 주문을 접수한다. 성공 시 서버가 발급한 order_no/order_id 를 돌려준다.
 * 예외를 던지지 않고 판별 가능한 결과 객체를 반환한다(호출 측 UX 단순화).
 */
export async function placeCoreOrder(input: PlaceOrderInput): Promise<PlaceOrderResult> {
  if (!CORE_URL || !CORE_ANON) {
    return { ok: false, code: 'config' };
  }

  const endpoint = `${CORE_URL.replace(/\/+$/, '')}/functions/v1/core-place-order`;

  const body = {
    channel: 'rxk_web' as const,
    payment_method: input.paymentMethod || 'cod',
    customer: {
      name: input.customer.name,
      phone: input.customer.phone,
      address: input.customer.address,
      note: input.customer.note ?? '',
      email: input.customer.email || undefined,
      city: input.customer.city,
    },
    items: input.items.map((it) => ({ sku: it.sku, qty: it.qty })),
    coupon_code: input.couponCode || undefined,
    ...getFbCookies(),
    // Where the visitor came from (Facebook, a /go/ link…) for the Analytics page.
    attribution: getAttribution(),
  };

  // 15초 타임아웃 — 멈춘 네트워크에서 무한 로딩 방지
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    apikey: CORE_ANON,
    Authorization: `Bearer ${CORE_ANON}`,
  };
  if (input.customerAccessToken) {
    headers['X-Customer-Token'] = input.customerAccessToken;
  }

  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch {
    clearTimeout(timer);
    return { ok: false, code: 'network' };
  }
  clearTimeout(timer);

  let data: Record<string, unknown> = {};
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    // 파싱 실패지만 상태코드로 판별 계속
  }

  if (res.ok && data.ok === true) {
    return {
      ok: true,
      orderNo: (data.order_no as string | null) ?? null,
      orderId: String(data.order_id ?? ''),
      discountAmountBdt: Number(data.discount_amount_bdt ?? 0),
      shippingBdt: Number(data.shipping_bdt ?? 0),
    };
  }

  if (res.status === 409 || data.error === 'insufficient_stock') {
    return { ok: false, code: 'insufficient_stock' };
  }
  if (data.error === 'cod_unavailable_repeat_cancellations') {
    return { ok: false, code: 'cod_blocked' };
  }
  if (res.status >= 400 && res.status < 500) {
    return { ok: false, code: 'validation' };
  }
  return { ok: false, code: 'server' };
}

// =====================================================================
// 온라인 결제 게이트웨이(SSLCommerz 등) 흐름
// ---------------------------------------------------------------------
//  COD와 달리 결제가 확정되기 전에는 core_orders가 생성되지 않는다.
//  initGatewayPayment()는 결제 세션만 열고(gatewayUrl), 실제 주문 생성은
//  결제 확인 후 서버(core-sslcommerz-callback)에서 일어난다 — 그래서
//  체크아웃 페이지는 이 시점에 영수증 사본을 저장하거나 장바구니를
//  비우면 안 된다(결제가 실패/취소되면 장바구니가 그대로 남아 있어야
//  한다). 결제 결과 페이지(/checkout/payment/:tranId)가 완료를 확인한
//  뒤에야 savePendingCheckoutSnapshot()로 남겨둔 정보 + 서버가 돌려준
//  order_no로 saveReceiptCopy()를 호출한다.
// =====================================================================

export interface InitGatewayPaymentResult {
  ok: true;
  tranId: string;
  gatewayUrl: string;
}
export interface InitGatewayPaymentFailure {
  ok: false;
  code: PlaceOrderErrorCode;
}
export type InitGatewayPaymentOutcome = InitGatewayPaymentResult | InitGatewayPaymentFailure;

/**
 * 결제 게이트웨이(SSLCommerz) 세션을 연다. 성공하면 브라우저를 보낼
 * gatewayUrl과, 결제 완료 후 상태를 조회할 tranId를 돌려준다.
 */
export async function initGatewayPayment(input: PlaceOrderInput): Promise<InitGatewayPaymentOutcome> {
  if (!CORE_URL || !CORE_ANON) {
    return { ok: false, code: 'config' };
  }

  const endpoint = `${CORE_URL.replace(/\/+$/, '')}/functions/v1/core-init-gateway-payment`;

  const body = {
    channel: 'rxk_web' as const,
    payment_method: input.paymentMethod || '',
    customer: {
      name: input.customer.name,
      phone: input.customer.phone,
      address: input.customer.address,
      note: input.customer.note ?? '',
      email: input.customer.email || undefined,
      city: input.customer.city,
    },
    items: input.items.map((it) => ({ sku: it.sku, qty: it.qty })),
    coupon_code: input.couponCode || undefined,
    ...getFbCookies(),
    // Where the visitor came from (Facebook, a /go/ link…) for the Analytics page.
    attribution: getAttribution(),
  };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    apikey: CORE_ANON,
    Authorization: `Bearer ${CORE_ANON}`,
  };
  if (input.customerAccessToken) {
    headers['X-Customer-Token'] = input.customerAccessToken;
  }

  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch {
    clearTimeout(timer);
    return { ok: false, code: 'network' };
  }
  clearTimeout(timer);

  let data: Record<string, unknown> = {};
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    // 파싱 실패지만 상태코드로 판별 계속
  }

  if (res.ok && data.ok === true && typeof data.gateway_url === 'string') {
    return { ok: true, tranId: String(data.tran_id ?? ''), gatewayUrl: data.gateway_url };
  }

  if (res.status === 409 || data.error === 'insufficient_stock') {
    return { ok: false, code: 'insufficient_stock' };
  }
  if (res.status >= 400 && res.status < 500) {
    return { ok: false, code: 'validation' };
  }
  return { ok: false, code: 'server' };
}

export interface GatewayPaymentStatus {
  status: 'initiated' | 'completed' | 'failed' | 'cancelled';
  orderNo: string | null;
  orderId: string | null;
  // 'completed'일 때만 채워진다 — 서버가 실제로 확정한 금액(영수증용).
  subtotalBdt: number | null;
  shippingBdt: number | null;
  discountBdt: number | null;
}

/** 결제 결과 페이지가 폴링하는 용도 — 서버가 최종 확정한 상태만 돌려준다. */
export async function checkGatewayPaymentStatus(tranId: string): Promise<GatewayPaymentStatus | null> {
  if (!CORE_URL || !CORE_ANON || !tranId) return null;

  const endpoint = `${CORE_URL.replace(/\/+$/, '')}/functions/v1/core-check-payment-status`;
  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: CORE_ANON,
        Authorization: `Bearer ${CORE_ANON}`,
      },
      body: JSON.stringify({ tran_id: tranId }),
    });
    const data = (await res.json()) as Record<string, unknown>;
    if (!res.ok || data.ok !== true) return null;
    return {
      status: data.status as GatewayPaymentStatus['status'],
      orderNo: (data.order_no as string | null) ?? null,
      orderId: (data.order_id as string | null) ?? null,
      subtotalBdt: (data.subtotal_bdt as number | null) ?? null,
      shippingBdt: (data.shipping_bdt as number | null) ?? null,
      discountBdt: (data.discount_bdt as number | null) ?? null,
    };
  } catch {
    return null;
  }
}

// =====================================================================
// 주문 영수증 사본 저장(localStorage) — 정본은 항상 서버(core_orders)다.
// 체크아웃 페이지와 결제 결과 페이지가 공유해서 쓴다.
// =====================================================================

const ORDER_STORAGE_KEY = 'rxk-orders';

function loadOrders(): Array<Record<string, unknown>> {
  try {
    const raw = localStorage.getItem(ORDER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Array<Record<string, unknown>>) : [];
  } catch {
    return [];
  }
}

export function saveReceiptCopy(order: Record<string, unknown>): void {
  try {
    const orders = loadOrders();
    orders.unshift(order);
    localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(orders));
  } catch {
    // 영수증 사본 저장 실패는 비치명 — 서버에 이미 접수됨.
  }
}

// =====================================================================
// 결제창으로 나가기 직전의 체크아웃 정보를 임시 보관(sessionStorage).
// 결제가 성공해 돌아왔을 때만 saveReceiptCopy()로 옮겨 적는다 — 결제가
// 실패/취소되면 그냥 버려지고 장바구니는 그대로 남는다.
// =====================================================================

export interface PendingCheckoutSnapshot {
  items: { slug: string; qty: number }[];
  contact: { fullName: string; phone: string; email?: string };
  shipping: {
    address1: string;
    address2?: string;
    city: string;
    area?: string;
    postalCode?: string;
    deliveryNote?: string;
  };
  paymentMethod: string;
}

const PENDING_CHECKOUT_PREFIX = 'rxk-pending-checkout-';

export function savePendingCheckoutSnapshot(tranId: string, snapshot: PendingCheckoutSnapshot): void {
  try {
    sessionStorage.setItem(PENDING_CHECKOUT_PREFIX + tranId, JSON.stringify(snapshot));
  } catch {
    // 실패해도 결제 자체는 계속 진행 — 결과 페이지가 스냅샷 없이도 order_no는 보여줄 수 있다.
  }
}

export function loadPendingCheckoutSnapshot(tranId: string): PendingCheckoutSnapshot | null {
  try {
    const raw = sessionStorage.getItem(PENDING_CHECKOUT_PREFIX + tranId);
    return raw ? (JSON.parse(raw) as PendingCheckoutSnapshot) : null;
  } catch {
    return null;
  }
}

export function clearPendingCheckoutSnapshot(tranId: string): void {
  try {
    sessionStorage.removeItem(PENDING_CHECKOUT_PREFIX + tranId);
  } catch {
    // 무시 — 청소 실패는 비치명적
  }
}
